import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Printer, Truck } from "lucide-react";
import api, { inr } from "../lib/api";
import { Button } from "../components/ui/button";

const SLOT_LABEL = { "same-day": "Same Day Delivery", "next-day": "Next Day Delivery", "store-pickup": "Store Pickup" };

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/orders/track/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => setErr(true));
  }, [orderNumber]);

  if (err) return <div className="max-w-2xl mx-auto px-4 py-24 text-center"><h1 className="font-serif text-3xl text-forest">Order not found</h1></div>;
  if (!order) return <div className="max-w-2xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12 fade-in" data-testid="order-confirmation">
      <div className="text-center mb-8">
        <CheckCircle2 size={56} className="text-forest mx-auto mb-4" />
        <h1 className="font-serif text-4xl text-forest">Thank you, {order.customer.name.split(" ")[0]}!</h1>
        <p className="text-muted-foreground mt-2">Your order has been confirmed. We'll deliver freshness to your door.</p>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden" id="invoice">
        <div className="bg-forest text-cream px-6 py-5 flex items-center justify-between">
          <div>
            <div className="font-serif text-2xl text-gold">Aadvan Dry Fruits</div>
            <div className="text-xs text-cream/60">Tax Invoice / Order Receipt</div>
          </div>
          <div className="text-right text-sm">
            <div className="text-cream/60 text-xs">Order No.</div>
            <div className="font-medium" data-testid="confirmation-order-number">{order.order_number}</div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid sm:grid-cols-2 gap-4 text-sm mb-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Deliver To</div>
              <div className="text-forest">{order.customer.name}</div>
              <div className="text-muted-foreground">{order.customer.phone}</div>
              <div className="text-muted-foreground">{order.customer.address}, {order.customer.pincode}</div>
            </div>
            <div className="sm:text-right">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Delivery</div>
              <div className="text-forest flex items-center sm:justify-end gap-1"><Truck size={14} /> {SLOT_LABEL[order.delivery_slot]}</div>
              <div className="text-muted-foreground">Payment: {order.payment_method}</div>
              <div className="text-muted-foreground">{new Date(order.created_at).toLocaleString("en-IN")}</div>
            </div>
          </div>

          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-muted-foreground text-xs uppercase tracking-wider">
              <th className="py-2">Item</th><th className="py-2 text-center">Qty</th><th className="py-2 text-right">Price</th></tr></thead>
            <tbody>
              {order.items.map((i, idx) => (
                <tr key={idx} className="border-b border-border">
                  <td className="py-3 text-forest">{i.name} <span className="text-muted-foreground">({i.variant})</span></td>
                  <td className="py-3 text-center">{i.qty}</td>
                  <td className="py-3 text-right">{inr(i.price * i.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 space-y-1 text-sm max-w-xs ml-auto">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{inr(order.subtotal)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-forest"><span>Discount</span><span>− {inr(order.discount)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{order.delivery_charge === 0 ? "Free" : inr(order.delivery_charge)}</span></div>
            <div className="flex justify-between font-serif text-lg text-forest border-t border-border pt-2"><span>Total</span><span>{inr(order.total)}</span></div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 justify-center mt-8">
        <Button onClick={() => window.print()} variant="outline" data-testid="print-invoice" className="rounded-full border-forest text-forest hover:bg-forest hover:text-cream"><Printer size={16} className="mr-2" /> Print Invoice</Button>
        <Link to={`/track-order?order=${order.order_number}`}><Button className="bg-forest text-cream rounded-full" data-testid="go-track-order">Track Order</Button></Link>
        <Link to="/products"><Button className="bg-gold text-forest hover:bg-gold-soft rounded-full">Continue Shopping</Button></Link>
      </div>
    </div>
  );
}
